import { Big } from 'big.js';

import type { Currency } from './currency';
import type { MoneyField } from './money-field';
import type { BigSource, Comparison } from 'big.js';

// Do not allow conversions to/from number
// to ensure all money calculations are forced to be done
// within the Big number space
// However, set strict to false to allow mul/div with a decimal factor

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Big.strict = false;

// Re-exports to hide the Big.js dependency
export type Percentage = BigSource;
export type MoneyNumber = BigSource;

/**
 * A class to manipulate money values in a safe way (no floating point errors).
 */
export class Money {
  private big: Big;
  private currency: Currency;
  private static HUNDRED = new Big('100');
  private static ONE = new Big('1');

  // Helper, but try to avoid as this enforces EUR everywhere
  public static EUR_ZERO = new Money('0', 'EUR');

  private constructor(cents: BigSource, currency: Currency) {
    this.big = new Big(cents);
    this.currency = currency;
  }

  /** Convert a currency string (e.g. from the TAF API) to Money */
  public static fromWhole(amount: string, currency: Currency = 'EUR'): Money {
    return new Money(amount.replace(/_/g, ''), currency).mul(100);
  }

  /** Convert from the smallest denomination of the currency to Money object
   * Note: currency defaults to EUR because, which is a risk:
   * fixing all errors once we go multi-currency.
   */
  public static fromCents(cents: string, currency: Currency = 'EUR'): Money {
    return new Money(cents.replace(/_/g, ''), currency);
  }

  /** Convert and round the smallest denomination of the currency */
  public toCents(): string {
    return this.big.round(0).valueOf();
  }

  public toNumber(): Big {
    return new Big(this.big);
  }

  public toFixed(n: number): string {
    return this.big.div(100).toFixed(n);
  }

  /** Convert and round to base currency value, e.g. whole euros iso. cents */
  public toWhole(): string {
    return this.big.round(0).div('100').valueOf();
  }

  public toString(): string {
    return this.currency.toUpperCase() + ' ' + this.toWhole();
  }

  public toField(): MoneyField {
    return { cents: this.big.valueOf(), currency: this.currency };
  }

  public static fromField({ cents, currency }: MoneyField): Money {
    return new Money(cents, currency);
  }

  public toJSON(): MoneyField {
    return this.toField();
  }

  public static fromJSON(json: string): Money {
    const obj: MoneyField = JSON.parse(json);
    return new Money(obj.cents, obj.currency);
  }

  public getCurrency(): Currency {
    return this.currency;
  }

  /** 'reviver' function passed as second arg to JSON.parse()
   * to instantiate Money objects for all money fields in the JSON
   * Note: we may want to look at https://github.com/typestack/class-transformer
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  public static reviver(key: string, value: any): any {
    try {
      const obj = typeof value === 'string' ? JSON.parse(value) : value;
      if (obj.cents && obj.currency && Object.keys(obj).length === 2 && key) {
        return Money.fromField(obj);
      }
    } catch (_err) {
      return value;
    }
    return value;
  }

  /** 'replacer' function passed as second arg to JSON.stringify()
   * to convert all Money values to the MoneyField and round down
   * the values to cents.
   * Note: we may want to look at https://github.com/typestack/class-transformer
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  public static toCentsReplacer(this: any, key: string, value: any): any {
    // Note: Money.toJSON() is called on value before the replacer is called, so use this[key] instead.
    const m = this[key];
    if (m instanceof Money) {
      return { cents: m.toCents(), currency: m.getCurrency() };
    }
    return value;
  }

  public add(money: Money): Money {
    if (this.currency != money.currency) {
      throw new Error('Cannot add different currencies');
    }
    return new Money(this.big.add(money.big), this.currency);
  }

  public sub(money: Money): Money {
    if (this.currency != money.currency) {
      throw new Error('Cannot subtract different currencies');
    }
    return new Money(this.big.sub(money.big), this.currency);
  }

  /**
   * @returns Rounds up to whole cents
   */
  public ceil(): Money {
    return new Money(this.big.round(0, 3), this.currency);
  }

  public floor(): Money {
    return new Money(this.big.round(0, 0), this.currency);
  }

  public round(): Money {
    return new Money(this.big.round(0), this.currency);
  }

  /**
   * @returns Rounds up to whole currency
   */
  public ceilWhole(): Money {
    return this.div(100).ceil().mul(100);
  }

  public ceilToNextWholeMultiple(interval: number): Money {
    return this.div(interval).ceilWhole().mul(interval);
  }

  public floorToNextWholeMultiple(interval: number): Money {
    return this.div(interval).floorWhole().mul(interval);
  }

  /**
   * @returns Rounds down to whole currency
   */
  public floorWhole(): Money {
    return this.div(100).floor().mul(100);
  }

  public roundWhole(): Money {
    return this.div(100).round().mul(100);
  }

  public mul(factor: BigSource): Money {
    return new Money(this.big.mul(factor), this.currency);
  }

  public div(factor: BigSource): Money {
    return new Money(this.big.div(factor), this.currency);
  }

  public static sum(moneys: Money[], currency: Currency): Money {
    const zero = Money.fromCents('0', currency);
    return moneys.reduce((aggregator, money) => aggregator.add(money), zero);
  }

  public mulPercentage(percentagePoints: Percentage): Money {
    return new Money(this.big.mul(percentagePoints).div('100'), this.currency);
  }

  public divPercentage(percentagePoints: Percentage): Money {
    return new Money(this.big.mul(Money.HUNDRED).div(percentagePoints), this.currency);
  }

  public addSurplus(percentagePoints: Percentage): Money {
    return new Money(this.big.mul(Money.HUNDRED.add(percentagePoints)).div(Money.HUNDRED), this.currency);
  }

  public removeSurplus(percentagePoints: Percentage): Money {
    return new Money(this.big.mul(Money.HUNDRED).div(Money.HUNDRED.add(percentagePoints)), this.currency);
  }

  public addDiscount(percentagePoints: Percentage): Money {
    return new Money(this.big.mul(Money.HUNDRED.sub(percentagePoints)).div(Money.HUNDRED), this.currency);
  }

  public removeDiscount(percentagePoints: Percentage): Money {
    return new Money(this.big.mul(Money.HUNDRED).div(Money.HUNDRED.sub(percentagePoints)), this.currency);
  }

  public gt(money: Money): boolean {
    return this.cmp(money) > 0;
  }

  public gtZero(): boolean {
    return this.big.cmp('0') > 0;
  }

  public gte(money: Money): boolean {
    return this.cmp(money) > -1;
  }

  public gteZero(): boolean {
    return this.big.cmp('0') > -1;
  }

  public lt(money: Money): boolean {
    return this.cmp(money) < 0;
  }

  public lte(money: Money): boolean {
    return this.cmp(money) < 1;
  }

  public lteZero(): boolean {
    return this.big.lte('0');
  }

  public ltZero(): boolean {
    return this.big.lt('0');
  }

  public eq(money: Money): boolean {
    return this.currency === money.currency && this.big.eq(money.big);
  }

  public isBetween(min: Money, max: Money): boolean {
    return this.gte(min) && this.lte(max);
  }

  public eqZero(): boolean {
    return this.big.eq('0');
  }

  public isMultipleOf(money: Money): boolean {
    return this.big.mod(money.big).eq('0');
  }

  public discountPercentagePoints(discountedPrice: Money): Big {
    return Money.ONE.minus(discountedPrice.div(this.toNumber()).toNumber()).times(Money.HUNDRED);
  }

  public percentagePointsOf(price: Money): Big {
    return this.big.eq(0) ? Money.HUNDRED : Money.ONE.div(this.big).mul(price.big).mul(Money.HUNDRED);
  }

  public static max(moneyA: Money, moneyB: Money): Money {
    return moneyA.gte(moneyB) ? moneyA : moneyB;
  }

  public static min(moneyA: Money, moneyB: Money): Money {
    return moneyA.lte(moneyB) ? moneyA : moneyB;
  }

  public cmp(money: Money): Comparison {
    if (this.currency != money.currency) {
      throw new Error('Cannot compare different currencies');
    }
    return this.big.cmp(money.big);
  }

  public abs(): Money {
    return new Money(this.big.abs(), this.currency);
  }
}
